import { useMemo } from "react";
import { X, Dumbbell, Apple, ShieldAlert, Heart, LayoutGrid } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { usePrograms } from "@/hooks/usePrograms";
import { useNutritionProfile } from "@/hooks/useNutritionProfile";
import { useNutritionPlan } from "@/hooks/useNutritionPlan";
import { useWellnessCheckins } from "@/hooks/useWellnessCheckins";
import { useAthleteProfile } from "@/hooks/useAthleteProfile";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

// ── Shared helpers ──

function SectionPlaceholder({ title, description }: { title: string; description: string }) {
  return (
    <Card className="border-dashed border-border">
      <CardContent className="py-6 flex flex-col items-center text-center gap-2">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="text-xs text-muted-foreground/70">{description}</p>
        <Badge variant="secondary" className="text-[10px] mt-1">Bientôt</Badge>
      </CardContent>
    </Card>
  );
}

function TabSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

// ── Sport Tab ──

function SportTabContent() {
  const { programs, programsCount, loading, currentProgramId } = usePrograms();
  const navigate = useNavigate();
  const currentProgram = programs?.find((p) => p.id === currentProgramId) ?? null;

  if (loading) return <TabSkeleton />;

  const activePrograms = programs?.filter((p) => p.status === "active") ?? [];
  const totalSessions = currentProgram?.content_json?.weekly_plan
    ? Object.values(currentProgram.content_json.weekly_plan).flat().length
    : 0;

  return (
    <div className="space-y-4">
      {currentProgram ? (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-foreground">{currentProgram.title}</p>
              <Badge variant="secondary" className="text-[10px]">Actif</Badge>
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>{currentProgram.duration_weeks} sem.</span>
              <span>{totalSessions} séances/sem.</span>
              <span>v{currentProgram.version}</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-border">
          <CardContent className="py-6 text-center">
            <p className="text-sm text-muted-foreground">Aucun programme sport actif</p>
            <Button size="sm" variant="outline" className="mt-2 min-h-[44px]" onClick={() => navigate("/programs?tab=generate")}>
              Créer un programme
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-foreground">Mes programmes sport</p>
            <Badge variant="outline" className="text-[10px]">{programsCount ?? 0}</Badge>
          </div>
          {activePrograms.length === 0 ? (
            <p className="text-xs text-muted-foreground">Aucun programme créé</p>
          ) : (
            <ul className="space-y-2 max-h-48 overflow-y-auto">
              {activePrograms.slice(0, 5).map((p) => (
                <li key={p.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground truncate max-w-[200px]">{p.title}</span>
                  <Badge variant={p.id === currentProgram?.id ? "default" : "secondary"} className="text-[10px]">
                    {p.id === currentProgram?.id ? "Actif" : p.status}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" variant="ghost" className="w-full min-h-[44px] text-xs" onClick={() => navigate("/programs")}>
            Voir tous les programmes →
          </Button>
        </CardContent>
      </Card>

      <SectionPlaceholder title="Contenu" description="Séances · Exercices · Stats détaillées" />
    </div>
  );
}

// ── Nutrition Tab ──

function NutritionTabContent() {
  const { profile, isCompleted, loading } = useNutritionProfile();
  const { hasPlan, plan, isGenerating, generatedAt } = useNutritionPlan();
  const navigate = useNavigate();

  if (loading) return <TabSkeleton />;

  return (
    <div className="space-y-4">
      {isCompleted ? (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-4 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-foreground">Profil nutrition configuré</p>
              <Badge variant="secondary" className="text-[10px]">✅ Actif</Badge>
            </div>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span>{profile.diet_type ?? "Omnivore"}</span>
              <span>·</span>
              <span>{profile.nutrition_goal ?? "Objectif non défini"}</span>
            </div>
            {profile.meal_times?.length > 0 && (
              <p className="text-xs text-muted-foreground">{profile.meal_times.length} repas/jour</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-border">
          <CardContent className="py-6 text-center space-y-2">
            <span className="text-3xl">🥗</span>
            <p className="text-sm text-muted-foreground">Profil nutrition non configuré</p>
            <Button size="sm" variant="outline" className="min-h-[44px]" onClick={() => navigate("/wellness?tab=nutrition")}>
              Configurer (2 min)
            </Button>
          </CardContent>
        </Card>
      )}

      {isCompleted && hasPlan && plan ? (
        <Card>
          <CardContent className="py-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Planning alimentaire</p>
            <p className="text-xs text-muted-foreground">
              Généré le {generatedAt ? new Date(generatedAt).toLocaleDateString("fr-FR") : "—"}
            </p>
            <Button size="sm" variant="ghost" className="w-full min-h-[44px] text-xs" onClick={() => navigate("/wellness?tab=nutrition")}>
              Voir le planning complet →
            </Button>
          </CardContent>
        </Card>
      ) : isCompleted ? (
        <Card className="border-dashed border-border">
          <CardContent className="py-4 text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              {isGenerating ? "Génération en cours…" : "Aucun planning généré"}
            </p>
            <Button size="sm" variant="outline" className="min-h-[44px]" onClick={() => navigate("/wellness?tab=nutrition")}>
              {isGenerating ? "Voir la progression" : "Générer mon planning"}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <SectionPlaceholder title="Contenu" description="Repas · Recettes · Macros détaillés" />
    </div>
  );
}

// ── Prévention Tab ──

function PreventionTabContent() {
  const { isLoading, painZones, recoveryScore } = useWellnessCheckins();
  const navigate = useNavigate();

  if (isLoading) return <TabSkeleton />;

  const recoveryConfig = {
    good: { label: "Bon", color: "text-primary", emoji: "🟢" },
    medium: { label: "Moyen", color: "text-yellow-500", emoji: "🟡" },
    attention: { label: "Attention", color: "text-destructive", emoji: "🔴" },
    unknown: { label: "—", color: "text-muted-foreground", emoji: "⚪" },
  };
  const rc = recoveryConfig[recoveryScore.level];

  return (
    <div className="space-y-4">
      {/* Section 1 — Score récupération */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-foreground">Score de récupération</p>
            <span className={cn("text-sm font-bold", rc.color)}>{rc.emoji} {rc.label}</span>
          </div>
          {recoveryScore.avgSleep !== null && (
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Sommeil : {Math.round(recoveryScore.avgSleep * 10) / 10}h</span>
              <span>Douleurs : {recoveryScore.painCount}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2 — Zones douloureuses */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <p className="text-sm font-medium text-foreground">Zones douloureuses cette semaine</p>
          {painZones.length === 0 ? (
            <p className="text-sm text-primary flex items-center gap-2">
              <span>✅</span> Aucune douleur signalée
            </p>
          ) : (
            <ul className="space-y-2">
              {painZones.map((pz) => (
                <li key={pz.zone} className="flex items-center justify-between text-sm">
                  <span className="text-foreground capitalize">{pz.zone}</span>
                  <span className={cn(
                    "font-medium",
                    pz.count >= 3 ? "text-destructive" : pz.count >= 2 ? "text-orange-500" : "text-yellow-500"
                  )}>
                    {pz.count >= 3 ? "🔴" : pz.count >= 2 ? "🟠" : "🟡"} {pz.count}×
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Button size="sm" variant="ghost" className="w-full min-h-[44px] text-xs" onClick={() => navigate("/prevention")}>
            Voir la page prévention →
          </Button>
        </CardContent>
      </Card>

      {/* Section 3 — Contenu */}
      <SectionPlaceholder title="Contenu" description="Séances récup · Étirements · Sommeil" />
    </div>
  );
}

// ── Well-being Tab ──

function WellbeingTabContent() {
  const { isLoading, todayCheckin, last7Days, checkins } = useWellnessCheckins();
  const navigate = useNavigate();

  if (isLoading) return <TabSkeleton />;

  // Hygiene from localStorage
  const hygieneKey = "wellness_hygiene_v1";
  const hygieneDays = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(hygieneKey) || "[]"); } catch { return []; }
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const todayHygiene = hygieneDays.find((d: any) => d.date === today);
  const hygieneComplete = todayHygiene?.hydration && todayHygiene?.sleep && todayHygiene?.mental;

  const weeklyHygieneCount = last7Days.filter((date: string) => {
    const entry = hygieneDays.find((d: any) => d.date === date);
    return entry?.hydration && entry?.sleep && entry?.mental;
  }).length;

  return (
    <div className="space-y-4">
      {/* Section 1 — Statut du jour */}
      <Card className={cn("border-primary/20", hygieneComplete ? "bg-primary/5" : "")}>
        <CardContent className="py-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-foreground">Hygiène du jour</p>
            {hygieneComplete ? (
              <Badge variant="secondary" className="text-[10px]">✅ Complet</Badge>
            ) : (
              <Badge variant="outline" className="text-[10px]">En cours</Badge>
            )}
          </div>
          <div className="flex gap-3 text-xs">
            <span>{todayHygiene?.hydration ? "💧 ✓" : "💧 ✗"}</span>
            <span>{todayHygiene?.sleep ? "😴 ✓" : "😴 ✗"}</span>
            <span>{todayHygiene?.mental ? "🧘 ✓" : "🧘 ✗"}</span>
          </div>
        </CardContent>
      </Card>

      {/* Section 2 — Régularité */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <p className="text-sm font-medium text-foreground">Régularité cette semaine</p>
          <p className="text-sm text-foreground">
            <span className="font-bold">{weeklyHygieneCount}/7</span> jours complets
          </p>
          <div className="flex gap-1">
            {last7Days.map((date: string) => {
              const entry = hygieneDays.find((d: any) => d.date === date);
              const complete = entry?.hydration && entry?.sleep && entry?.mental;
              return (
                <div
                  key={date}
                  className={cn(
                    "h-4 w-4 rounded-full",
                    complete ? "bg-primary" : "bg-muted-foreground/20"
                  )}
                  title={date}
                />
              );
            })}
          </div>
          <Button size="sm" variant="ghost" className="w-full min-h-[44px] text-xs" onClick={() => navigate("/wellness?tab=hygiene")}>
            Gérer mes habitudes →
          </Button>
        </CardContent>
      </Card>

      {/* Section 3 — Contenu */}
      <SectionPlaceholder title="Contenu" description="Routines · Habitudes · Suivi détaillé" />
    </div>
  );
}

// ── Général / Estimatoire Tab ──

function GeneralTabContent() {
  const { programs, loading: programsLoading, currentProgramId } = usePrograms();
  const { isCompleted: nutritionDone, loading: nutritionLoading } = useNutritionProfile();
  const { isLoading: wellnessLoading, recoveryScore } = useWellnessCheckins();
  const { isOnboardingComplete, loading: profileLoading } = useAthleteProfile();
  const navigate = useNavigate();

  const loading = programsLoading || nutritionLoading || wellnessLoading || profileLoading;
  if (loading) return <TabSkeleton />;

  const currentProgram = programs?.find((p) => p.id === currentProgramId) ?? null;
  const activeCount = programs?.filter((p) => p.status === "active").length ?? 0;
  const showSuperProgram = activeCount >= 2;

  // Estimatory data for missing sections
  const modules = [
    { key: "sport", label: "Sport", done: !!currentProgram, detail: currentProgram ? currentProgram.title : "Estimé : 3 séances/sem." },
    { key: "nutrition", label: "Nutrition", done: nutritionDone, detail: nutritionDone ? "Profil configuré" : "Estimé : 2000 kcal/j" },
    { key: "prevention", label: "Prévention", done: recoveryScore.level !== "unknown", detail: recoveryScore.level !== "unknown" ? `Récup : ${recoveryScore.level}` : "Estimé : récup standard" },
    { key: "wellbeing", label: "Bien-être", done: false, detail: "Estimé : 3 habitudes/j" },
  ];

  const estimatedCount = modules.filter((m) => !m.done).length;

  return (
    <div className="space-y-4">
      {/* Section 1 — Vue d'ensemble */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-foreground">Programme Général</p>
            {estimatedCount > 0 && (
              <Badge variant="outline" className="text-[10px]">{estimatedCount} estimé{estimatedCount > 1 ? "s" : ""}</Badge>
            )}
          </div>
          <div className="space-y-2">
            {modules.map((m) => (
              <div key={m.key} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{m.label}</span>
                <span className={cn("font-medium", m.done ? "text-primary" : "text-muted-foreground italic")}>
                  {m.done ? "✅" : "📊"} {m.detail}
                </span>
              </div>
            ))}
          </div>
          {estimatedCount > 0 && (
            <p className="text-[10px] text-muted-foreground/70 italic">
              Les valeurs estimées seront remplacées par tes données réelles quand tu configureras chaque module.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Section 2 — Super-Programme */}
      {showSuperProgram ? (
        <Card className="border-primary/30 bg-primary/10">
          <CardContent className="py-4 space-y-2">
            <p className="text-sm font-bold text-foreground">🚀 Super-Programme disponible</p>
            <p className="text-xs text-muted-foreground">
              Tu as {activeCount} programmes actifs. Le Super-Programme peut les orchestrer ensemble.
            </p>
            <SectionPlaceholder title="" description="Activation du Super-Programme — Bientôt" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-4 space-y-2">
            <p className="text-sm font-medium text-foreground">Super-Programme</p>
            <p className="text-xs text-muted-foreground">
              Crée au moins 2 programmes pour débloquer le Super-Programme qui les combine intelligemment.
            </p>
            <div className="flex gap-2 text-xs text-muted-foreground">
              <span>{activeCount}/2 programmes actifs</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section 3 — Actions */}
      <Card>
        <CardContent className="py-4 space-y-2">
          <p className="text-sm font-medium text-foreground">Compléter ton profil</p>
          <div className="space-y-2">
            {!currentProgram && (
              <Button size="sm" variant="outline" className="w-full min-h-[44px] text-xs justify-start gap-2" onClick={() => navigate("/programs?tab=generate")}>
                <Dumbbell className="h-4 w-4" /> Créer un programme sport
              </Button>
            )}
            {!nutritionDone && (
              <Button size="sm" variant="outline" className="w-full min-h-[44px] text-xs justify-start gap-2" onClick={() => navigate("/wellness?tab=nutrition")}>
                <Apple className="h-4 w-4" /> Configurer la nutrition
              </Button>
            )}
            {!isOnboardingComplete && (
              <Button size="sm" variant="outline" className="w-full min-h-[44px] text-xs justify-start gap-2" onClick={() => navigate("/athlete-profile")}>
                <LayoutGrid className="h-4 w-4" /> Compléter ton profil athlète
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main drawer ──

interface ProgramsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function ProgramsDrawer({ open, onClose }: ProgramsDrawerProps) {
  if (!open) return null;

  const allTabs = [
    { key: "general", label: "Général", icon: <LayoutGrid className="h-4 w-4" /> },
    { key: "sport", label: "Sport", icon: <Dumbbell className="h-4 w-4" /> },
    { key: "nutrition", label: "Nutrition", icon: <Apple className="h-4 w-4" /> },
    { key: "prevention", label: "Prévention", icon: <ShieldAlert className="h-4 w-4" /> },
    { key: "wellbeing", label: "Well-being", icon: <Heart className="h-4 w-4" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={cn(
        "relative ml-auto w-full sm:max-w-lg bg-background border-l border-border shadow-xl",
        "flex flex-col h-full overflow-hidden",
        "animate-in slide-in-from-right duration-300"
      )}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-lg font-bold text-foreground">Programmes</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="min-h-[44px] min-w-[44px]">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <Tabs defaultValue="general" className="space-y-4">
            <TabsList className="w-full grid grid-cols-5 h-auto">
              {allTabs.map((tab) => (
                <TabsTrigger key={tab.key} value={tab.key} className="gap-1 text-xs px-1 py-2.5 min-h-[44px] flex flex-col items-center">
                  {tab.icon}
                  <span className="truncate">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="general"><GeneralTabContent /></TabsContent>
            <TabsContent value="sport"><SportTabContent /></TabsContent>
            <TabsContent value="nutrition"><NutritionTabContent /></TabsContent>
            <TabsContent value="prevention"><PreventionTabContent /></TabsContent>
            <TabsContent value="wellbeing"><WellbeingTabContent /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default ProgramsDrawer;
